/**
 * The live remaining-time reading an appliance card shows.
 *
 * `utils/applianceRuntime` holds the decision table; this composable feeds it
 * the three things it cannot know by itself — the device's live values (over
 * the socket), its automation configs, and a clock for the timer sources.
 *
 * The clock only runs while the device actually has a deadline to count down.
 * A washer reporting `remaining_time` already updates once a minute from the
 * hub, so ticking a second-hand there would re-render the card for text that
 * cannot change.
 */
import { computed, onUnmounted, ref, watch, type ComputedRef, type Ref } from 'vue';
import { useDeviceValues } from './useDeviceValues';
import { useDeviceAutomationsStore } from '../stores/deviceAutomations';
import { loadStateOf, readApplianceRuntime, toFiniteNumber, type ApplianceRuntime } from '../utils/applianceRuntime';

/**
 * The largest remaining time each device has reported this session — the
 * denominator an appliance's own `remaining_time` does not carry. Keyed by
 * device id, and dropped when a cycle ends, so the next wash starts from its
 * own peak rather than the last one's.
 */
const peakSecondsByDevice = new Map<string, number>();

/** Only for tests: forget the peaks a previous case recorded. */
export function resetApplianceRuntimePeaks(): void {
  peakSecondsByDevice.clear();
}

export function useApplianceRuntime(
  deviceId: string,
  seedValues?: () => readonly { valueKey?: string | null; currentValue?: unknown }[] | undefined,
  /** Values the caller already keeps live (a widget's own `useDeviceValues`),
   *  so a card does not open a second subscription for the same device. */
  existingValues?: Ref<Record<string, unknown>>,
): {
  runtime: ComputedRef<ApplianceRuntime | null>;
  running: ComputedRef<boolean>;
  loadState: ComputedRef<'on' | 'off' | 'unknown'>;
  unlit: ComputedRef<boolean>;
} {
  const automationsStore = useDeviceAutomationsStore();
  // A caller that already keeps this device's values live (`SwitchWidget` does)
  // hands them over, so the card does not open a second socket subscription for
  // the same device. The call is skipped rather than duplicated.
  const own = existingValues ? null : useDeviceValues(deviceId);
  const currentValues = existingValues ?? own!.currentValues;
  const initializeValues = own?.initializeValues;
  const nowMs = ref(Date.now());

  const seeded = seedValues?.();
  if (seeded?.length) initializeValues?.(seeded);
  if (seedValues) {
    watch(
      seedValues,
      (rows) => {
        if (rows?.length) initializeValues?.(rows);
      },
      { deep: true },
    );
  }

  void automationsStore.fetchDeviceAutomationSummaries(deviceId, false);

  /** A deadline the hub spelled as a timestamp — the only source that needs a clock. */
  const hasDeadline = computed(() => {
    const scheduled = currentValues.value['switch_off_timer__scheduled_off_at'];
    return typeof scheduled === 'string' && scheduled.trim() !== '';
  });

  let ticker: ReturnType<typeof setInterval> | null = null;

  function startTicker(): void {
    if (ticker !== null) return;
    ticker = setInterval(() => {
      nowMs.value = Date.now();
    }, 1000);
  }

  function stopTicker(): void {
    if (ticker === null) return;
    clearInterval(ticker);
    ticker = null;
  }

  watch(hasDeadline, (needed) => (needed ? startTicker() : stopTicker()), { immediate: true });
  onUnmounted(stopTicker);

  // A running appliance reports a fresh peak — a new cycle resets the ring.
  watch(
    () => toFiniteNumber(currentValues.value['remaining_time']),
    (minutes) => {
      const seconds = minutes !== null && minutes > 0 ? minutes * 60 : null;
      const peak = peakSecondsByDevice.get(deviceId) ?? null;
      if (seconds === null) {
        peakSecondsByDevice.delete(deviceId);
        return;
      }
      if (peak === null || seconds > peak) peakSecondsByDevice.set(deviceId, seconds);
    },
    { immediate: true },
  );

  const runtime = computed<ApplianceRuntime | null>(() =>
    readApplianceRuntime({
      values: currentValues.value,
      automations: automationsStore.getDeviceAutomationSummaries(deviceId),
      nowMs: nowMs.value,
      peakSeconds: peakSecondsByDevice.get(deviceId) ?? null,
    }),
  );

  /** On, off, or nothing to say — the picture's grey filter keys off this. */
  const loadState = computed(() => loadStateOf(currentValues.value));

  /** Running = a cycle with time on it, or the load's own on/off value. */
  const running = computed(() => runtime.value !== null || loadState.value === 'on');

  /** Known off: the card greys its picture. A device with no load state at all
   *  (a fridge, a NAS volume) keeps its colour — it cannot be "off". */
  const unlit = computed(() => loadState.value === 'off' && runtime.value === null);

  return { runtime, running, loadState, unlit };
}
